import type { ChatPartRendererProps } from '@mui/x-chat/headless';

import { isDefined, isTruthy } from 'common';

import { AgenticChatActivityDisclosure } from './Disclosure';
import { AgenticChatActivityPart } from './Part';
import {
  type AgenticChatActivityMessagePart,
  isAgenticChatActivityPartVisible,
} from './isVisiblePart';
import { formatDuration } from '../../../../utils/time';

const toolOutcomes = new Map([
  ['code-execute', 'ran code'],
  ['get-schema', 'looked up a schema'],
  ['list-capabilities', 'listed capabilities'],
  ['read-file', 'read a skill file'],
  ['search-capabilities', 'searched capabilities'],
]);

interface AgenticChatActivityWorkSummaryProps extends ChatPartRendererProps<AgenticChatActivityMessagePart> {
  workParts: { part: AgenticChatActivityMessagePart; offset: number }[];
  runState: 'Running' | 'Complete';
  nowMilliseconds: number;
  focus: 'Restore' | 'Preserve';
  onFocusChange: (focus: 'Focused' | 'Blurred') => void;
}

export function AgenticChatActivityWorkSummary(props: AgenticChatActivityWorkSummaryProps) {
  const { message, index, onToolCall, workParts, runState, nowMilliseconds, focus, onFocusChange } =
    props;
  // Older histories without persisted run timing omit the duration rather than estimating it.
  let durationSeconds: number | undefined;
  if (
    runState === 'Running' &&
    isDefined(message.metadata) &&
    'runStartedAtMilliseconds' in message.metadata &&
    typeof message.metadata.runStartedAtMilliseconds === 'number'
  ) {
    durationSeconds = Math.floor(
      Math.max(0, nowMilliseconds - message.metadata.runStartedAtMilliseconds) / 1_000
    );
  } else if (
    isDefined(message.metadata) &&
    'runDurationMilliseconds' in message.metadata &&
    typeof message.metadata.runDurationMilliseconds === 'number' &&
    !message.parts
      .slice(0, index)
      .some((part, previousIndex) =>
        isAgenticChatActivityPartVisible({ part, message, index: previousIndex })
      )
  ) {
    durationSeconds = Math.max(1, Math.round(message.metadata.runDurationMilliseconds / 1_000));
  }

  const outcomes = Array.from(
    new Set(
      workParts.flatMap(({ part }) =>
        part.type === 'reasoning' ? [] : [part.toolInvocation.toolName]
      )
    ),
    (toolName) => toolOutcomes.get(toolName) ?? `used ${toolName.replaceAll('-', ' ')}`
  );
  const actions = `${outcomes.slice(0, 3).join(', ')}${outcomes.length > 3 ? '…' : ''}`;
  const durationLabel = isDefined(durationSeconds)
    ? formatDuration({ duration: durationSeconds, format: 'Compact' })
    : undefined;
  const labelParts: string[] = [];
  if (runState === 'Running' || isDefined(durationLabel) || !isTruthy(actions.length)) {
    labelParts.push(
      `${runState === 'Running' ? 'Working' : 'Worked'}${isDefined(durationLabel) ? ` for ${durationLabel}` : ''}`
    );
  }
  if (isTruthy(actions.length)) {
    labelParts.push(`${actions.charAt(0).toUpperCase()}${actions.slice(1)}`);
  }

  return (
    <AgenticChatActivityDisclosure
      focus={focus}
      onFocusChange={(focus) => onFocusChange(focus)}
      label={labelParts.join(' · ')}
      animation={runState === 'Running' ? 'Active' : 'Inactive'}
      errorCount={
        workParts.filter(
          ({ part }) =>
            part.type !== 'reasoning' &&
            ['output-error', 'output-denied'].includes(part.toolInvocation.state)
        ).length
      }
    >
      {workParts.map(({ part, offset }) => (
        <AgenticChatActivityPart
          key={
            part.type === 'reasoning'
              ? `reasoning-${index + offset}`
              : `tool-${part.toolInvocation.toolCallId}`
          }
          part={part}
          message={message}
          index={index + offset}
          onToolCall={(payload) => onToolCall?.(payload)}
        />
      ))}
    </AgenticChatActivityDisclosure>
  );
}
